(function () {
  'use strict';
  if (customElements.get('siss-bfcm-header')) return;

  const deadlines = new Map();
  const readStored = key => {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); }
    catch (_) { return null; }
  };
  const writeStored = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); }
    catch (_) {}
  };

  customElements.define('siss-bfcm-header', class extends HTMLElement {
    connectedCallback() {
      // An early-loaded controller can connect before the HTML parser adds children.
      // Reconnect once parsing finishes, including headers inserted later in the page.
      if (this.ownerDocument.readyState === 'loading' && !this.onDocumentReady) {
        this.onDocumentReady = () => {
          this.onDocumentReady = null;
          if (this.isConnected) this.connectedCallback();
        };
        this.ownerDocument.addEventListener('DOMContentLoaded', this.onDocumentReady, { once: true });
      }
      clearInterval(this.interval);
      if (this.dataset.bfcmCountdownSource !== undefined) {
        Promise.resolve().then(() => {
          if (!this.isConnected) return;
          for (const timer of this.ownerDocument?.querySelectorAll?.('siss-bfcm-header[data-countdown-key]') || []) {
            if (timer !== this && timer.dataset.countdownKey === this.dataset.countdownKey) timer.connectedCallback();
          }
        });
      }
      this.connectSummary();
      this.timer = this.querySelector('[data-bfcm-header-timer]');
      if (!this.timer) return;
      this.timer.hidden = true;
      const sharedSource = this.dataset.countdownKey
        ? this.ownerDocument?.querySelector?.('[data-bfcm-countdown-source]') : null;
      const config = sharedSource && sharedSource.dataset.countdownKey === this.dataset.countdownKey ? sharedSource.dataset : this.dataset;
      if (config.countdownEnabled !== 'true') return;

      const inputs = [config.countdownDays, config.countdownHours, config.countdownMinutes];
      if (inputs.some(value => typeof value !== 'string' || value.trim() === '')) return;
      const values = inputs.map(Number);
      if (values.some(value => !Number.isSafeInteger(value) || value < 0)) return;
      const duration = (values[0] * 86400 + values[1] * 3600 + values[2] * 60) * 1000;
      if (!Number.isSafeInteger(duration) || duration <= 0) return;

      this.parts = ['days', 'hours', 'minutes', 'seconds'].map(part => this.querySelector('[data-bfcm-header-' + part + ']'));
      if (this.parts.some(part => !part)) return;
      // Shared campaign cycles do not depend on a visitor's first visit or saved timers.
      const anchorText = (config.countdownAnchor || '').trim();
      if (anchorText) {
        if (!/(?:Z|[+-]\d{2}:\d{2})$/i.test(anchorText)) return;
        const anchor = Date.parse(anchorText);
        if (!Number.isSafeInteger(anchor) || anchor <= 0) return;
        this.repeat = config.countdownRepeat === 'true';
        this.countdownRecord = { anchor, duration };
        if (this.tick()) this.interval = setInterval(() => this.tick(), 1000);
        return;
      }
      const resetMinutes = Number(config.countdownResetMinutes || 0);
      const resetBefore = Number.isSafeInteger(resetMinutes) && resetMinutes >= 0 && resetMinutes * 60000 < duration ? resetMinutes * 60000 : 0;
      const signature = values.join('-') + (resetBefore ? ':reset-' + resetMinutes : '');
      const baseKey = this.dataset.countdownKey
        ? 'sissilux:bfcm-countdown:' + encodeURIComponent(this.dataset.countdownKey)
        : 'sissilux:bfcm-header:' + encodeURIComponent(this.dataset.campaignId || '') + ':' + encodeURIComponent(this.dataset.blockId || '');
      const key = baseKey + ':' + signature;
      const currentKey = baseKey + ':current';
      const current = deadlines.get(baseKey) || readStored(currentKey);
      let saved = current?.signature === signature ? current : null;
      if (!current) saved = readStored(key);
      const now = Date.now();
      if (!saved || saved.signature !== signature || saved.duration !== duration || !Number.isSafeInteger(saved.endsAt) || saved.endsAt <= 0) {
        saved = { signature, duration, endsAt: now + duration };
      }
      if (!Number.isSafeInteger(saved.endsAt)) return;
      deadlines.set(baseKey, saved);
      writeStored(currentKey, saved);
      writeStored(key, saved);
      this.endsAt = saved.endsAt;
      this.repeat = config.countdownRepeat === 'true';
      this.countdownRecord = { baseKey, key, currentKey, signature, duration, resetBefore };

      if (this.tick()) this.interval = setInterval(() => this.tick(), 1000);
    }

    connectSummary() {
      this.disconnectSummary();
      if (this.dataset.summaryEnabled !== 'true') return;
      this.titleElement = this.querySelector('[data-bfcm-header-title]');
      this.value = this.querySelector('[data-bfcm-header-value]');
      this.eyebrow = this.querySelector('[data-bfcm-header-eyebrow]');
      this.subtitle = this.querySelector('[data-bfcm-header-subtitle]');
      if (!this.titleElement || !this.value) return;
      if (this.originalTitle === undefined) {
        this.originalTitle = this.dataset.baseTitle ?? this.titleElement.textContent;
        this.originalSubtitleHidden = this.dataset.baseSubtitleHidden === undefined
          ? (this.subtitle?.hidden || false) : this.dataset.baseSubtitleHidden === 'true';
      }
      this.summaryDocument = this.ownerDocument || (typeof document !== 'undefined' ? document : null);
      this.onRewardState = event => {
        const state = this.readRewardView(event.target, event.detail?.presentation);
        if (state) this.renderSummary(state);
      };
      this.summaryDocument?.addEventListener('sissilux:bfcm-state', this.onRewardState);
      this.updateSummary();
      if (typeof queueMicrotask === 'function') queueMicrotask(() => {
        if (this.isConnected && this.summaryDocument) this.updateSummary();
      });
    }

    readRewardView(view, eventPresentation = null) {
      if (!view?.matches?.('[data-bfcm-rewards]') || String(view.dataset.blockId || '') !== String(this.dataset.blockId || '')) return null;
      try {
        const sourceText = view.querySelector('[data-gpt-gift-state]')?.textContent;
        const state = JSON.parse(sourceText || 'null');
        if (state?.campaignId !== this.dataset.campaignId) return null;
        const presentation = eventPresentation || view.giftSummaryPresentation;
        if (typeof sourceText === 'string' && presentation?.sourceText === sourceText
          && presentation.state?.campaignId === state.campaignId) return presentation.state;
        return state;
      } catch (_) { return null; }
    }

    updateSummary() {
      const scope = this.closest?.('cart-drawer') || this.summaryDocument;
      const views = scope?.querySelectorAll?.('[data-bfcm-rewards]') || [];
      for (const view of views) {
        const state = this.readRewardView(view);
        if (state) { this.renderSummary(state); return; }
      }
      if (this.dataset.selectionMode === 'tiered') {
        this.renderSummary({selectionMode:'tiered', country:this.dataset.country,
          campaignEnabled:this.dataset.campaignEnabled !== 'false', claimsEnabled:this.dataset.claimsEnabled !== 'false',
          eligibleQuantity:this.dataset.eligibleQuantity, activeTier:this.dataset.activeTier,
          giftAllowance:this.dataset.giftAllowance, selectedGiftQuantity:this.dataset.selectedGiftQuantity,
          selectedGiftValueCents:this.dataset.selectedGiftValueCents, maxGiftValueCents:this.dataset.maxGiftValueCents,
          fixedGiftQuantity:this.dataset.fixedGiftQuantity, fixedGiftValueCents:this.dataset.fixedGiftValueCents,
          confirmedFixedGiftQuantity:this.dataset.confirmedFixedGiftQuantity,
          choiceLimit:this.dataset.choiceLimit, choiceSelectedQuantity:this.dataset.choiceSelectedQuantity,
          choiceSelectedValueCents:this.dataset.choiceSelectedValueCents,
          giftCardValueCents:this.dataset.giftCardValueCents, giftValueCurrency:this.dataset.giftValueCurrency});
        return;
      }
      this.renderSummary({selectionMode:'multiple', country:'US', campaignEnabled:true, claimsEnabled:true,
        giftAllowance:this.dataset.giftAllowance, selectedGiftQuantity:this.dataset.selectedGiftQuantity,
        selectedGiftValueCents:this.dataset.selectedGiftValueCents, maxGiftValueCents:this.dataset.maxGiftValueCents,
        giftCardValueCents:this.dataset.giftCardValueCents, giftValueCurrency:this.dataset.giftValueCurrency});
    }

    renderSummary(state) {
      const integer = value => {
        if (value === null || value === undefined || value === '') return null;
        const number = Number(value);
        return Number.isSafeInteger(number) && number >= 0 ? number : null;
      };
      if (state.selectionMode === 'tiered') {
        this.renderTieredSummary(state, integer);
        return;
      }
      const allowance = integer(state.giftAllowance);
      if (state.selectionMode !== 'multiple' || state.country !== 'US' || !state.campaignEnabled || !state.claimsEnabled || !allowance) {
        this.titleElement.textContent = this.originalTitle;
        this.value.hidden = true;
        if (this.eyebrow) this.eyebrow.hidden = true;
        if (this.subtitle) this.subtitle.hidden = this.originalSubtitleHidden;
        this.classList?.remove('is-reward-summary');
        return;
      }
      const rawSelected = integer(state.selectedGiftQuantity) ?? 0;
      const resetting = Boolean(window.__gptGiftControllerV2?.resetting) || rawSelected > allowance;
      const selected = resetting ? 0 : rawSelected;
      const remaining = allowance - selected;
      const unitValue = integer(state.maxGiftValueCents);
      const selectedValue = resetting ? 0 : integer(state.selectedGiftValueCents);
      const currency = String(state.giftValueCurrency || '').toUpperCase();
      const money = value => {
        if (!Number.isSafeInteger(value) || value < 0 || !/^[A-Z]{3}$/.test(currency)) return '';
        try { return new Intl.NumberFormat('en-US', {style:'currency', currency, minimumFractionDigits:0, maximumFractionDigits:2}).format(value / 100); }
        catch (_) { return ''; }
      };
      const words = {
        allowance, selected, remaining,
        gift_word:(selected || allowance) === 1 ? (this.dataset.giftSingular || 'gift') : (this.dataset.giftPlural || 'gifts'),
        max_value:unitValue === null ? '' : money(unitValue * allowance),
        selected_value:selectedValue === null ? '' : money(selectedValue),
        remaining_value:unitValue === null ? '' : money(unitValue * remaining),
        card_value:money(integer(state.giftCardValueCents))
      };
      const copy = (key, fallback) => String(this.dataset[key] || fallback).replace(/\{([a-z_]+)\}/g, (match, name) => Object.hasOwn(words, name) ? String(words[name]) : match);
      let headline, value;
      if (selected === 0) {
        headline = copy('chooseTemplate', 'Choose {allowance} FREE {gift_word}');
        value = words.max_value ? copy('valueMaxTemplate', 'FREE {gift_word} worth up to {max_value}') : '';
      } else if (remaining === 0) {
        headline = copy('selectedTemplate', '{selected} FREE {gift_word} selected');
        value = words.selected_value ? copy('valueSelectedTemplate', 'FREE {gift_word} worth {selected_value}') : '';
      } else {
        headline = copy('partialTemplate', '{selected} of {allowance} selected · Choose {remaining} more');
        value = words.selected_value ? copy('valuePartialTemplate', 'FREE {gift_word} worth {selected_value}') : '';
      }
      if (integer(state.giftCardValueCents) > 0 && words.card_value) value = [value, copy('cardValueTemplate', '+ {card_value} gift card')].filter(Boolean).join(' ');
      this.titleElement.textContent = headline;
      this.value.textContent = value;
      this.value.hidden = !value;
      if (this.eyebrow) this.eyebrow.hidden = false;
      if (this.subtitle) this.subtitle.hidden = true;
      this.classList?.add('is-reward-summary');
    }

    renderTieredSummary(state, integer) {
      const eligible = integer(state.eligibleQuantity);
      const allowance = integer(state.giftAllowance);
      if (!state.campaignEnabled || !state.claimsEnabled || eligible === 0
        || (eligible === null && !(integer(state.activeTier) > 0)) || !allowance) {
        this.titleElement.textContent = this.originalTitle;
        this.value.hidden = true;
        if (this.eyebrow) this.eyebrow.hidden = true;
        if (this.subtitle) this.subtitle.hidden = this.originalSubtitleHidden;
        this.classList?.remove('is-reward-summary');
        return;
      }
      const currency = String(state.giftValueCurrency || '').toUpperCase();
      const locale = state.locale || this.dataset.locale || this.summaryDocument?.documentElement?.lang || undefined;
      const money = value => {
        if (!Number.isSafeInteger(value) || value < 0 || !/^[A-Z]{3}$/.test(currency)) return '';
        try { return new Intl.NumberFormat(locale, {style:'currency', currency, minimumFractionDigits:0}).format(value / 100); }
        catch (_) { return ''; }
      };
      const copy = (key, datasetKey, fallback, replacements = {}) => String(state.promotion?.copy?.[key] || this.dataset[datasetKey] || fallback)
        .replace(/\{([a-z_]+)\}/g, (match, name) => Object.hasOwn(replacements, name) ? String(replacements[name]) : match);
      let headline = allowance === 1
        ? copy('giftSummarySingular', 'tierGiftSummarySingular', '{count} FREE gift', {count:allowance})
        : copy('giftSummaryPlural', 'tierGiftSummaryPlural', '{count} FREE gifts', {count:allowance});
      const cardAmount = integer(state.giftCardValueCents), cardValue = money(cardAmount);
      if (cardAmount > 0 && cardValue) {
        const cardCopy = copy('inlineCard', 'tierInlineCardTemplate', '{amount} gift card', {amount:cardValue});
        headline = [headline, /^\+\s/.test(cardCopy) ? cardCopy : '+ ' + cardCopy].filter(Boolean).join(' ');
      }
      this.titleElement.textContent = headline;
      this.value.textContent = '';
      this.value.hidden = true;
      if (this.eyebrow) this.eyebrow.hidden = false;
      if (this.subtitle) this.subtitle.hidden = true;
      this.classList?.add('is-reward-summary');
    }

    disconnectSummary() {
      if (this.summaryDocument && this.onRewardState) this.summaryDocument.removeEventListener('sissilux:bfcm-state', this.onRewardState);
      this.summaryDocument = null;
      this.onRewardState = null;
    }

    tick() {
      if (!this.isConnected) {
        clearInterval(this.interval);
        return false;
      }
      const now = Date.now();
      const record = this.countdownRecord;
      if (record.anchor) {
        // Recompute after sleep, missed cycles or clock corrections without drifting.
        const cycles = this.repeat && now >= record.anchor ? Math.floor((now - record.anchor) / record.duration) + 1 : 0;
        this.endsAt = record.anchor + cycles * record.duration;
      } else {
      const shared = deadlines.get(record.baseKey);
      if (shared?.signature === record.signature) this.endsAt = shared.endsAt;
      if (this.repeat && now >= this.endsAt - record.resetBefore) {
        // Renew early without displaying the final hour. Legacy zero-boundary timers retain their cycle.
        this.endsAt = record.resetBefore > 0 ? now + record.duration
          : this.endsAt + (Math.floor((now - this.endsAt) / record.duration) + 1) * record.duration;
        const saved = { signature: record.signature, duration: record.duration, endsAt: this.endsAt };
        deadlines.set(record.baseKey, saved);
        writeStored(record.currentKey, saved);
        writeStored(record.key, saved);
      }
      }
      const remaining = Math.max(0, Math.ceil((this.endsAt - now) / 1000));
      if (remaining === 0) {
        this.timer.hidden = true;
        clearInterval(this.interval);
        return false;
      }
      const values = [
        Math.floor(remaining / 86400),
        Math.floor(remaining % 86400 / 3600),
        Math.floor(remaining % 3600 / 60),
        remaining % 60
      ];
      this.parts.forEach((part, index) => {
        part.textContent = index === 0 && this.dataset.countdownPadDays !== 'true' ? String(values[index]) : String(values[index]).padStart(2, '0');
      });
      this.timer.hidden = false;
      return true;
    }

    disconnectedCallback() {
      if (this.onDocumentReady) this.ownerDocument.removeEventListener('DOMContentLoaded', this.onDocumentReady);
      this.onDocumentReady = null;
      this.disconnectSummary();
      clearInterval(this.interval);
      this.interval = null;
    }
  });
})();

